export const LEGACY_THOR_V2_SPECS = [
  {
    category: "Camera System",
    item: "Cameras",
    positioning: "2 x 1080P cameras, 1920 x 1080, 2 MP, 30 FPS",
  },
  {
    category: "IMU",
    item: "Motion Sensing",
    positioning: "Six-axis IMU at 300 Hz; 3 us alignment with image frames",
  },
  {
    category: "Power",
    item: "System Power",
    positioning: "Approximately 2.5 W",
  },
  {
    category: "Runtime",
    item: "Continuous Operation",
    positioning: "Approximately 6 hours; hot-swappable battery support",
  },
  {
    category: "Weight",
    item: "Device Weight",
    positioning: "Approximately 180 g",
  },
  {
    category: "Thermal",
    item: "Cooling",
    positioning: "Passive cooling",
  },
  {
    category: "Platform",
    item: "Compatibility",
    positioning: "iOS, Android, Windows, and macOS",
  },
  {
    category: "Updates",
    item: "Upgrade Paths",
    positioning: "USB, Wi-Fi, and SD card updates with anti-brick protection",
  },
];

export const LEGACY_THOR_V4_SPECS = [
  {
    category: "Camera System",
    item: "Cameras",
    positioning: "4 x 1080P cameras, 1920 x 1080, 2 MP, 30 FPS",
  },
  {
    category: "Synchronization",
    item: "Exposure Sync",
    positioning: "Sub-10 us hardware synchronization",
  },
  {
    category: "IMU",
    item: "Motion Sensing",
    positioning: "Six-axis IMU at 300 Hz; 3 us alignment with image frames",
  },
  {
    category: "Power",
    item: "System Power",
    positioning: "Approximately 5 W",
  },
  {
    category: "Runtime",
    item: "Continuous Operation",
    positioning: "Approximately 6 hours; hot-swappable battery support",
  },
  {
    category: "Weight",
    item: "Device Weight",
    positioning: "Approximately 500 g",
  },
  {
    category: "Thermal",
    item: "Cooling",
    positioning: "Active fan cooling",
  },
  {
    category: "Platform",
    item: "Compatibility",
    positioning: "iOS, Android, Windows, macOS; USB, Wi-Fi, and SD card updates",
  },
];

export const LEGACY_THOR_V6_SPECS = [
  {
    category: "Camera System",
    item: "Cameras",
    positioning: "6 x 1080P cameras, 1920 x 1080, 2 MP, 30 FPS",
  },
  {
    category: "Synchronization",
    item: "Exposure Sync",
    positioning: "Sub-10 us hardware synchronization",
  },
  {
    category: "IMU",
    item: "Motion Sensing",
    positioning: "Six-axis IMU at 300 Hz; 3 us alignment with image frames",
  },
  {
    category: "Power",
    item: "System Power",
    positioning: "Approximately 7.5 W",
  },
  {
    category: "Runtime",
    item: "Continuous Operation",
    positioning: "Approximately 6 hours; hot-swappable battery support",
  },
  {
    category: "Weight",
    item: "Device Weight",
    positioning: "Approximately 650 g",
  },
  {
    category: "Thermal",
    item: "Cooling",
    positioning: "Active fan cooling",
  },
  {
    category: "Platform",
    item: "Compatibility",
    positioning: "iOS, Android, Windows, macOS; USB, Wi-Fi, and SD card updates",
  },
];

export const LEGACY_PRODUCTS = [
  {
    slug: "thor-v2",
    path: "/products/thor-v2",
    kind: "hardware",
    title: "THOR V2",
    eyebrow: "Dual-camera visual-inertial capture",
    shortIntro: "Lightweight first-person capture for long wearable sessions and fast deployment.",
    description:
      "THOR V2 is a lightweight head-mounted visual-inertial capture device designed for long-session first-person recording, robot demonstrations, teleoperation, and human activity data.",
    specs: [
      "Balanced forehead camera unit and rear battery pack for long-session wear",
      "2 x 1080P synchronized cameras with 300 Hz six-axis IMU alignment",
      "Low-power passive cooling for quiet, lightweight, out-of-box capture",
    ],
    facts: [
      {
        value: "2 x 1080P",
        label: "30 FPS synchronized imagery",
      },
      {
        value: "300 Hz",
        label: "six-axis IMU",
      },
      {
        value: "3 us",
        label: "IMU-to-frame alignment",
      },
      {
        value: "~2.5 W",
        label: "system power",
      },
    ],
    applications: [
      {
        title: "First-Person Robot Data",
        description:
          "Naturally record how operators interact with tools, objects, and environments for embodied AI training.",
      },
      {
        title: "Teleoperation and Teaching",
        description:
          "Capture operator viewpoint, motion context, and task progress for replay, supervision, and model training.",
      },
      {
        title: "Human Activity Recording",
        description:
          "Support daily activity and long-duration field capture with a compact visual-inertial wearable.",
      },
    ],
    color: "#006AFF",
    visual: "/assets/images/thor-v2-product.png",
    icon: "capture",
    gallery: [
      {
        src: "/assets/images/thor-v2-product.png",
        alt: "THOR V2 complete product and wearable views",
        aspect: "aspect-[3/2]",
      },
    ],
    specTable: LEGACY_THOR_V2_SPECS,
  },
  {
    slug: "thor-v4",
    path: "/products/thor-v4",
    kind: "hardware",
    title: "THOR V4",
    eyebrow: "Quad-camera synchronized capture",
    shortIntro: "Wider multi-view capture with hardware synchronization and flexible split-body mounting.",
    description:
      "THOR V4 combines two THOR modules with a synchronization board to provide four-camera visual-inertial capture for wider coverage, strict timing, and flexible wearable or fixed mounting.",
    specs: [
      "Four-camera coverage reduces visual blind spots from a single viewing direction",
      "Sub-10 us hardware exposure synchronization across camera modules",
      "Split camera and external battery structure supports flexible mounting",
    ],
    facts: [
      {
        value: "4 x 1080P",
        label: "30 FPS synchronized imagery",
      },
      {
        value: "<10 us",
        label: "multi-camera exposure sync",
      },
      {
        value: "300 Hz",
        label: "six-axis IMU",
      },
      {
        value: "~5 W",
        label: "system power",
      },
    ],
    applications: [
      {
        title: "Wide-Angle Behavior Capture",
        description:
          "Record richer spatial relationships among operators, tools, objects, and surrounding environments.",
      },
      {
        title: "Multi-View Teleoperation",
        description: "Provide synchronized visual-inertial data for robot teaching and teleoperation tasks.",
      },
      {
        title: "Complex Scene Recording",
        description:
          "Support data capture where occlusion, fast movement, or multiple viewing directions matter.",
      },
    ],
    color: "#006AFF",
    visual: "/assets/images/thor-v4-product.png",
    icon: "capture",
    gallery: [
      {
        src: "/assets/images/thor-v4-product.png",
        alt: "THOR V4 complete product and wearable views",
        aspect: "aspect-[3/2]",
      },
    ],
    specTable: LEGACY_THOR_V4_SPECS,
  },
  {
    slug: "thor-v6",
    path: "/products/thor-v6",
    kind: "hardware",
    title: "THOR V6",
    eyebrow: "Six-camera full-coverage capture",
    shortIntro:
      "Full-coverage multi-view visual-inertial capture for complex tasks and motion-rich environments.",
    description:
      "THOR V6 combines three THOR modules through a synchronization board to provide six synchronized camera streams, broad spatial coverage, and stable capture for complex motion and mapping tasks.",
    specs: [
      "Six-camera coverage supports multi-directional scene understanding",
      "Sub-10 us hardware synchronization across all camera streams",
      "External high-capacity battery supports long continuous collection sessions",
    ],
    facts: [
      {
        value: "6 x 1080P",
        label: "30 FPS synchronized imagery",
      },
      {
        value: "<10 us",
        label: "multi-camera exposure sync",
      },
      {
        value: "300 Hz",
        label: "six-axis IMU",
      },
      {
        value: "~7.5 W",
        label: "system power",
      },
    ],
    applications: [
      {
        title: "Full-Body Motion Capture",
        description:
          "Record body motion and environment interaction from more directions to improve data completeness.",
      },
      {
        title: "Wearable Multi-View Capture",
        description:
          "Synchronize six visual streams for complex behavior learning and multi-view understanding.",
      },
      {
        title: "Complex Environment Mapping",
        description:
          "Provide visual-inertial input for spaces with fast motion, occlusion, and multi-directional structures.",
      },
    ],
    color: "#006AFF",
    visual: "/assets/images/thor-v6-product.png",
    icon: "capture",
    gallery: [
      {
        src: "/assets/images/thor-v6-product.png",
        alt: "THOR V6 complete product and wearable views",
        aspect: "aspect-[3/2]",
      },
    ],
    specTable: LEGACY_THOR_V6_SPECS,
  },
  {
    slug: "data-platform",
    path: "/products/data-platform",
    kind: "platform",
    title: "Data Platform",
    eyebrow: "AEgo Engine and data governance",
    shortIntro:
      "A data platform that turns raw multimodal capture into synchronized, governed, training-ready Physical AI assets.",
    description:
      "Data Platform connects AEgo Engine, NeuroScale, EGO-to-Robot conversion, and AEgo Edge Hub to transform raw physical interaction traces into usable robot learning data.",
    specs: [
      "Synchronizes multimodal capture streams across vision, IMU, audio, force-pattern, trajectory, and calibration data",
      "Cleans, anonymizes, segments, scores, and exports training-ready Physical AI datasets",
      "Supports local edge processing for privacy-sensitive environments and low-latency data workflows",
    ],
    color: "#006AFF",
    icon: "compiler",
    gallery: [],
  },
];

export const getLegacyProduct = (e) => LEGACY_PRODUCTS.find((t) => t.slug === e);
